import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Spin, Table, Tabs } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { getAllPackagesByResellerId } from "../../redux/slices/packageSlice";
import { AppDispatch, RootState } from "../../redux/store";
import {
  formatCurrency,
  formatDataGb,
  formatPlanName,
  getFlagUrl,
  getPlanSortKey,
  isRegionalPlan,
  normalizeDiscountPercent,
} from "./planFormat";

export function PosPackages() {
  const dispatch = useDispatch<AppDispatch>();
  const { packages, loading } = useSelector((state: RootState) => state.packages);
  const { user } = useSelector((state: RootState) => state.auth);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(getAllPackagesByResellerId());
  }, [dispatch]);

  const searchValue = search.toLowerCase();
  const filteredPackages = packages
    .filter(pkg => {
      const rawName = pkg.prepaidPackageTemplateName ?? "";
      const displayName = formatPlanName(rawName);
      return (
        rawName.toLowerCase().includes(searchValue) ||
        displayName.toLowerCase().includes(searchValue)
      );
    })
    .sort((a, b) =>
      getPlanSortKey(a.prepaidPackageTemplateName).localeCompare(
        getPlanSortKey(b.prepaidPackageTemplateName)
      )
    );

  const regionalPackages = filteredPackages.filter((pkg) =>
    isRegionalPlan(pkg.prepaidPackageTemplateName)
  );
  const localPackages = filteredPackages.filter(
    (pkg) => !isRegionalPlan(pkg.prepaidPackageTemplateName)
  );

  const columns = [
    {
      title: "Plan",
      dataIndex: "prepaidPackageTemplateName",
      key: "plan",
      render: (value: string) => {
        const displayName = formatPlanName(value);
        const flagUrl = getFlagUrl(value);
        return (
          <span className="plan-table__name">
            {flagUrl ? (
              <img
                src={flagUrl}
                alt={`${displayName} flag`}
                className="plan-table__flag"
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            ) : null}
            <span className="plan-table__name-text">{displayName}</span>
          </span>
        );
      },
    },
    {
      title: "Data",
      key: "data",
      render: (_: unknown, pkg: any) => formatDataGb(pkg.dataByte),
    },
    {
      title: "Networks Covered",
      key: "networks",
      render: () => "—",
    },
    {
      title: "Type",
      key: "type",
      render: () => (
        <span className="plan-table__type">
          <MobileOutlined className="plan-table__type-icon" />
          eSIM
        </span>
      ),
    },
    {
      title: "Validity",
      key: "validity",
      render: (_: unknown, pkg: any) => `${pkg.periodDays} Days`,
    },
    {
      title: "Retail Price",
      key: "price",
      align: "right" as const,
      render: (_: unknown, pkg: any) =>
        user?.showPrices && Number.isFinite(Number(pkg.cost))
          ? formatCurrency(Number(pkg.cost))
          : "—",
    },
    {
      title: "Your price",
      key: "discountedPrice",
      align: "right" as const,
      render: (_: unknown, pkg: any) => {
        if (!user?.showPrices) return "—";
        const cost = Number(pkg.cost);
        if (!Number.isFinite(cost)) return "—";
        const discount = normalizeDiscountPercent(
          pkg.resellerDiscountPercent ?? pkg.reseller_discount_percent
        );
        const discountedPrice = cost * (1 - discount / 100);
        return formatCurrency(discountedPrice);
      },
    },
    {
      title: "",
      key: "action",
      align: "right" as const,
      render: (_: unknown, pkg: any) => (
        <Link to={`/package/${pkg.id}`} className="plan-table__action">
          SELL
        </Link>
      ),
    },
  ];

  const renderTable = (dataSource: typeof filteredPackages) => (
    <>
      <Input
        placeholder="Search plans..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="plan-search"
        size="large"
      />
      <Table
        className="plan-table"
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        pagination={{ pageSize: 15, showSizeChanger: false }}
      />
    </>
  );

  const tabItems = [
    {
      key: "local",
      label: `Local (${localPackages.length})`,
      children: renderTable(localPackages),
    },
    {
      key: "regional",
      label: `Regional (${regionalPackages.length})`,
      children: renderTable(regionalPackages),
    },
  ];

  return (
    <div className="plans-page">
      {loading ? (
        <div className="page-loading">
          <Spin size="large" />
        </div>
      ) : (
        <Tabs className="plan-tabs" defaultActiveKey="local" items={tabItems} />
      )}
    </div>
  );
}

export default PosPackages;
